import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  gap: 12px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  color: #1f2937;
  font-size: 24px;
`;

const BackButton = styled.button`
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 14px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px;
`;

const CardLabel = styled.div`
  color: #6b7280;
  font-size: 12px;
`;

const CardValue = styled.div`
  color: #111827;
  font-size: 24px;
  font-weight: 700;
  margin-top: 6px;
`;

const ListCard = styled(Card)`
  margin-bottom: 12px;
`;

const ListTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 16px;
  color: #111827;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 18px;
`;

const Item = styled.li`
  margin: 5px 0;
  color: #374151;
  font-size: 14px;
`;

const Message = styled.div`
  background: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  padding: 12px;
`;

export const LoyaltyInsightsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [economy, setEconomy] = useState<any>(null);
  const [churn, setChurn] = useState<any[]>([]);
  const [clv, setClv] = useState<any[]>([]);
  const [recommendations, setRecommendations] = useState<any[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('auth_token');
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        };
        const [economyRes, churnRes, clvRes, recRes] = await Promise.all([
          fetch('/api/loyalty/economy/overview', { headers }),
          fetch('/api/loyalty/insights/churn', { headers }),
          fetch('/api/loyalty/insights/clv', { headers }),
          fetch('/api/loyalty/insights/recommendations', { headers }),
        ]);

        const economyData = await economyRes.json();
        const churnData = await churnRes.json();
        const clvData = await clvRes.json();
        const recData = await recRes.json();

        if (!economyRes.ok) {
          setError(economyData.message || 'Falha ao carregar insights.');
          return;
        }

        setEconomy(economyData.data || null);
        setChurn((churnData.data || []).slice(0, 8));
        setClv((clvData.data || []).slice(0, 8));
        setRecommendations((recData.data || []).slice(0, 8));
      } catch (err: any) {
        setError(err?.message || 'Erro de conexão ao carregar insights.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  return (
    <Container>
      <Header>
        <Title>Insights de Fidelização</Title>
        <BackButton onClick={() => navigate('/dashboard')}>Voltar ao Dashboard</BackButton>
      </Header>

      {error && <Message>{error}</Message>}

      {loading ? (
        <Card>Carregando insights...</Card>
      ) : (
        <>
          <Grid>
            <Card>
              <CardLabel>Pontos Emitidos</CardLabel>
              <CardValue>{economy?.points_issued ?? 0}</CardValue>
            </Card>
            <Card>
              <CardLabel>Pontos Resgatados</CardLabel>
              <CardValue>{economy?.points_redeemed ?? 0}</CardValue>
            </Card>
            <Card>
              <CardLabel>Breakage (%)</CardLabel>
              <CardValue>{economy?.breakage_rate_percent ?? 0}</CardValue>
            </Card>
            <Card>
              <CardLabel>ROI Estimado (%)</CardLabel>
              <CardValue>{economy?.estimated_roi_percent ?? '-'}</CardValue>
            </Card>
          </Grid>

          <ListCard>
            <ListTitle>Clientes com maior risco de churn</ListTitle>
            <List>
              {churn.map((item) => (
                <Item key={item.customer_id}>
                  {item.name} - score {item.churn_score} ({item.risk_level})
                </Item>
              ))}
            </List>
          </ListCard>

          <ListCard>
            <ListTitle>Segmentos de CLV</ListTitle>
            <List>
              {clv.map((item) => (
                <Item key={item.customer_id}>
                  {item.name} - CLV estimado {item.estimated_clv} ({item.segment})
                </Item>
              ))}
            </List>
          </ListCard>

          <ListCard>
            <ListTitle>Recomendações de oferta</ListTitle>
            <List>
              {recommendations.map((item, idx) => (
                <Item key={`${item.reward_id}-${idx}`}>
                  {item.reward_name} - {item.reason}
                </Item>
              ))}
            </List>
          </ListCard>
        </>
      )}
    </Container>
  );
};
